import * as React from 'react'
import { useCallback, useState } from 'react'
import { Theme, useTheme } from '@mui/material/styles'

import { currencyString, ENTITY_TYPES } from '@sales-center/common'
import type { ILead, ILeadDetail } from '@user/store/lead/typings'
import { InfoTooltip } from '@sales-center/components'
import type {
  IOpportunity,
  IOpportunityDetail,
} from '@user/store/opportunity/typings'
import { ServiceDialogEntityCardWrapper } from '@user/components/Services/ServiceDialogEntityCardWrapper'
import { ServiceDialogEntityDetailWrapper } from '@user/components/Services/ServiceDialogEntityDetailWrapper'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import Popover from '@mui/material/Popover'
import Typography from '@mui/material/Typography'
import { OPPORTUNITY_STATUSES } from '@user/components/Opportunities/OpportunityCard/typings'
import { LEAD_STATUSES } from '@user/components/Leads/LeadCard/typings'

const getStyles = (theme: Theme) => ({
  container: {
    width: '330px',
  },

  description: {
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(1),
    borderBottom: '1px dashed #979797',
    textDecoration: 'italic',
  },

  link: {
    color: '#005AA3',
    cursor: 'pointer',
  },

  popover: {
    pointerEvents: 'none',
  },

  popoverContent: {
    pointerEvents: 'auto',
    padding: theme.spacing(1),
  },

  tooltip: {
    fontSize: '14px',
    marginLeft: theme.spacing(0.5),
  },

  totalValue: {
    fontFamily: 'Source Sans Pro Bold',
    fontSize: '28px',
  },

  value: {
    fontFamily: 'Source Sans Pro Semibold',
    fontSize: '16px',
  },
})

interface Props {
  cardDetail?: ILead | IOpportunity
  entityType: ENTITY_TYPES
  entityStatus: LEAD_STATUSES | OPPORTUNITY_STATUSES
  entityDetail?: ILeadDetail | IOpportunityDetail
  initialValue: number
  recurringValue: number
  totalValue: number
}

export function TotalValuePopover(props: Props) {
  const {
    cardDetail,
    entityDetail,
    entityStatus,
    entityType,
    initialValue,
    recurringValue,
    totalValue,
  } = props

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)
  const id = open ? 'total-value-popover' : undefined

  const onClose = useCallback(() => setAnchorEl(null), [])
  const onOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
  }

  const [viewServicesDialogOpen, setViewServicesDialogOpen] = useState(false)
  const openViewServicesDialog = useCallback(
    () => setViewServicesDialogOpen(true),
    [],
  )
  const closeViewServicesDialog = useCallback(
    () => setViewServicesDialogOpen(false),
    [],
  )
  const styles = getStyles(useTheme())
  return (
    <>
      <IconButton
        color="primary"
        onClick={onOpen}
        size="small"
        data-test-id={`total-value-popover-button-${entityDetail?.id || cardDetail?.id}`}
      >
        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      </IconButton>
      <Popover
        id={id}
        open={open}
        onClose={onClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{ onMouseLeave: onClose, elevation: 3 }}
      >
        <Box p={2} sx={styles.container}>
          <Grid container direction="column" justifyContent="center">
            <Grid item container>
              <Box display="flex" alignItems="center" flexGrow={1} pr={3}>
                <Box mr={3}>
                  <Typography>Total Value</Typography>
                </Box>
                <Typography sx={styles.totalValue}>{`$${currencyString(
                  totalValue,
                )}`}</Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <IconButton
                  data-test-id={`total-value-popover-close-button-${entityDetail?.id || cardDetail?.id}`}
                  color="primary"
                  onClick={onClose}
                  size="small"
                >
                  <KeyboardArrowUpIcon />
                </IconButton>
              </Box>
            </Grid>
            <Grid item>
              <Box sx={styles.description}>
                <Typography variant="caption">
                  The total annual amount including the initial value (does not
                  include sales tax)
                </Typography>
              </Box>
            </Grid>
            <Grid item container>
              <ValueRow
                title="Initial Value"
                tooltipText={`The initial amount paid to initiate service (does not include sales tax)`}
                value={initialValue}
              />
              <ValueRow
                title="Annual Value"
                tooltipText={`The total recurring amount paid over a 1-year period (does not include sales tax)`}
                value={recurringValue}
              />

              {(!!cardDetail || !!entityDetail) && (
                <Grid item>
                  <Box pt={1}>
                    <Typography
                      sx={styles.link}
                      onClick={openViewServicesDialog}
                      data-test-id={`total-value-popover-view-services-${entityDetail?.id || cardDetail?.id}`}
                    >
                      View Services Added
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Box>
      </Popover>
      {!!cardDetail && (
        <ServiceDialogEntityCardWrapper
          detail={cardDetail}
          entityStatus={entityStatus}
          entityType={entityType}
          isOpen={viewServicesDialogOpen}
          onClose={closeViewServicesDialog}
        />
      )}
      {!!entityDetail && (
        <ServiceDialogEntityDetailWrapper
          detail={entityDetail}
          entityStatus={entityStatus}
          entityType={entityType}
          isOpen={viewServicesDialogOpen}
          onClose={closeViewServicesDialog}
        />
      )}
    </>
  )
}

interface ValueRowProps {
  title: string
  tooltipText: string
  value: number | string
}

function ValueRow(props: ValueRowProps) {
  const { title, tooltipText, value } = props
  const styles = getStyles(useTheme())
  return (
    <Grid item container>
      <Box display="flex" alignItems="center" flexGrow={1}>
        <Typography>{title}</Typography>
      </Box>
      <Box display="flex" alignItems="center">
        <Typography sx={styles.value}>{`$${currencyString(value)}`}</Typography>

        <InfoTooltip sx={styles.tooltip} infoText={tooltipText} />
      </Box>
    </Grid>
  )
}
